import React, { useState, useEffect, useRef } from 'react';
import { Check, MessageCircle, XCircle } from 'lucide-react';
import PopupComponent from '../../components/PopupComponent';
import ContactLinksGenerator from '../../utils/contactlinksgenerator';
import logger, { Logger } from '../../utils/logger';
import { shareContentLead } from '../../services/leadService';
import styles from './BulkShareModal.module.scss';

// Define the interfaces
interface ContentTemplate {
  _id: string;
  type: string;
  name?: string;
  details?: {
    title?: string;
    message?: string;
    description?: string;
    type?: string;
  };
  files?: Array<{
    fileName: string;
  }>;
}

const CONTENT_TYPES = ["message", "file", "page"];

interface Lead {
  _id: string;
  name?: string;
  phone?: string;
  email?: string;
}

interface BulkShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLeads: Lead[];
  contentTemplates: ContentTemplate[];
}

const BulkShareModal: React.FC<BulkShareModalProps> = ({
  isOpen,
  onClose,
  selectedLeads,
  contentTemplates,
}) => {
  const [contentType, setContentType] = useState<string>('message');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [completedLeads, setCompletedLeads] = useState<Record<string, boolean | 'loading'>>({});
  const [templatePreview, setTemplatePreview] = useState<string>('');
  const listRef = useRef<HTMLDivElement>(null);

  // Filter out leads with invalid phone numbers
  const validLeads = selectedLeads.filter(lead => {
    if (!lead.phone) {
      Logger.log(`Lead ${lead._id} (${lead.name || 'Unnamed'}) has no phone number`);
      return false;
    }
    
    // Basic phone validation
    const isValid = /^[+]?[\d\s()-]{8,}$/.test(lead.phone.trim());
    if (!isValid) {
      Logger.log(`Lead ${lead._id} (${lead.name || 'Unnamed'}) has invalid phone: ${lead.phone}`);
    }
    return isValid;
  });

  const totalLeads = validLeads.length;
  const completedCount = Object.keys(completedLeads).length;

  const filteredTemplates = contentTemplates.filter(template => {
    return template.type === contentType;
  });

  useEffect(() => {
    Logger.log("Content templates loaded:", contentTemplates.length);
  }, [contentTemplates]);

  useEffect(() => {
    // Update template preview when a template is selected
    if (selectedTemplate) {
      const template = contentTemplates.find(t => t._id === selectedTemplate);
      if (template) {
        // Special handling for file and page type previews
        if (template.type === 'file' || template.type === 'page') {
          const title = template.details?.title || 'Untitled';
          const fileInfo = template.type === 'file' && template.files && template.files.length > 0
            ? `File: ${template.files[0].fileName}`
            : '';
            
          setTemplatePreview(
            `Type: ${template.type.toUpperCase()}\n` +
            `Title: ${title}\n` +
            (fileInfo ? `${fileInfo}\n\n` : '\n') +
            `This ${template.type} will be shared with a unique link generated for each lead.`
          );
        } else {
          // Extract message based on template type
          let content = '';
          if (template.details?.message) {
            content = template.details.message;
          } else if (template.details?.description) {
            content = template.details.description;
          }
          
          // Show example with lead name replacement
          content = content.replace(/@lead name/g, "Example Lead");
          setTemplatePreview(content || 'No content available for this template');
        }
      }
    } else {
      setTemplatePreview('');
    }
  }, [selectedTemplate, contentTemplates]);

  const handleTemplateChange = (templateId: string) => {
    Logger.log('Template selected:', templateId);
    setSelectedTemplate(templateId);
  };

  const handleWhatsAppClick = async (lead: Lead) => {
    if (!selectedTemplate) {
      alert('Please select a content template first');
      return;
    }
  
    if (!lead.phone) {
      console.error('No phone number for lead:', lead);
      return;
    }
  
    try {
      // Show loading state
      const leadId = lead._id;
      setCompletedLeads(prev => ({
        ...prev,
        [leadId]: 'loading' 
      }));
  
      // Get the selected template
      const template = contentTemplates.find(t => t._id === selectedTemplate);
      if (!template) {
        console.error('Template not found:', selectedTemplate);
        return;
      }
  
      // Different handling based on content type
      let messageContent = '';
      
      // Special handling for file and page content types
      if (template.type === 'file' || template.type === 'page') {
        try {
          Logger.log(`Processing ${template.type} content for lead ${lead.name}`);
          
          // Call the shareContentLead API to generate a unique link
          const body = {
            contentId: template._id,
            leadIds: [lead._id],
            performedAt: new Date(),
            contentType: template.type,
          };
          
          const response = await shareContentLead(body);
          
          if (!response?.data?.data?.uniqueLink) {
            throw new Error('No unique link generated');
          }
          
          Logger.log(`Generated unique link for ${template.type}`);
          
          // Create message with the unique link
          messageContent = `Hi ${lead.name || "Customer"},
  
  Here's the link to view:
  ${response.data.data.uniqueLink}`;
        } catch (error) {
          console.error('Error generating share link:', error);
          alert('Failed to generate share link');
          
          // Reset completed state on error
          setCompletedLeads(prev => {
            const newState = {...prev};
            delete newState[leadId];
            return newState;
          });
          
          return;
        }
      } else {
        // Regular message handling for other content types
        if (template.details?.message) {
          messageContent = template.details.message;
        } else if (template.details?.description) {
          messageContent = template.details.description;
        }
        
        // Personalize the message with lead name
        messageContent = messageContent.replace(
          /@lead name/g, 
          lead.name || "Customer"
        );
      }
      
      // Proceed with WhatsApp sharing using the prepared message
      const contactGenerator = new ContactLinksGenerator({
        number: lead.phone,
        message: messageContent
      });
      
      // Open WhatsApp in a new window
      window.open(contactGenerator.whatsapp, '_blank');
  
      // Mark as completed
      setCompletedLeads(prev => ({
        ...prev,
        [lead._id]: true
      }));
  
      // Scroll to next unprocessed lead after a short delay
      setTimeout(() => {
        if (listRef.current) {
          const processedLeads = listRef.current.querySelectorAll('.lead-item');
          const nextUnprocessedIndex = Array.from(processedLeads).findIndex(
            (el) => !completedLeads[(el as HTMLElement).dataset.leadId as string] && 
                   (el as HTMLElement).dataset.leadId !== lead._id
          );
  
          if (nextUnprocessedIndex !== -1) {
            processedLeads[nextUnprocessedIndex].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
      }, 500);
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
    }
  };

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk WhatsApp Share"
      size="lg"
    >
      <div className={styles.container}>
        {/* Content Type Selection */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            Select Content Type
          </label>
          <select
            className={styles.formControl}
            value={contentType}
            onChange={(e) => {
              setContentType(e.target.value);
              logger.log(`Content type changed to: ${e.target.value}`);
            }}
          >
            {CONTENT_TYPES.map(type => (
              <option key={type} value={type}>
                {type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        {/* Template Selection */}
        {filteredTemplates.length > 0 ? (
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Select Content Template
            </label>
            <select
              className={styles.formControl}
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="">Select a template</option>
              {filteredTemplates.map((template) => (
                <option 
                  key={template._id} 
                  value={template._id}
                >
                  {template.details?.title || "Unnamed Template"}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className={styles.alertWarning}>
            <XCircle size={16} className={styles.alertIcon} />
            <span>
              No content available for selected type. Try another content type.
            </span>
          </div>
        )}

        {/* Template Preview */}
        {templatePreview && (
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Template Preview
            </label>
            <div className={styles.previewBox}>
              {templatePreview}
            </div>
          </div>
        )}

        {/* Progress Counter */}
        <div className={styles.progressContainer}>
          <span className={styles.progressLabel}>Progress</span>
          <span className={styles.progressCounter}>
            {completedCount} of {totalLeads} completed
          </span>
        </div>

        {/* Warning if some leads were filtered out */}
        {selectedLeads.length > validLeads.length && (
          <div className={styles.alertWarning}>
            <XCircle size={16} className={styles.alertIcon} />
            <span>
              {selectedLeads.length - validLeads.length} leads have invalid or missing phone numbers and won't be included.
            </span>
          </div>
        )}

        {/* Lead List */}
        <div 
          className={styles.leadList}
          ref={listRef}
        >
          {validLeads.length === 0 ? (
            <div className={styles.emptyState}>
              No leads with valid phone numbers found
            </div>
          ) : (
            validLeads.map((lead) => (
              <div
                key={lead._id}
                data-lead-id={lead._id}
                className={`${styles.leadItem} ${
                  completedLeads[lead._id] ? styles.leadItemCompleted : ''
                }`}
              >
                <div className={styles.leadInfo}>
                  <div className={styles.leadName}>{lead.name || 'Unnamed Lead'}</div>
                  <div className={styles.leadPhone}>{lead.phone}</div>
                </div>
                <div>
                  <button
                    onClick={() => handleWhatsAppClick(lead)}
                    className={`${styles.whatsappButton} ${
                      completedLeads[lead._id] === true
                        ? styles.buttonSuccess
                        : completedLeads[lead._id] === 'loading'
                        ? styles.buttonLoading
                        : selectedTemplate
                          ? styles.buttonPrimary
                          : styles.buttonDisabled
                    }`}
                    disabled={!selectedTemplate || completedLeads[lead._id] === true || completedLeads[lead._id] === 'loading'}
                  >
                    {completedLeads[lead._id] === true ? (
                      <>
                        <Check size={16} className={styles.buttonIcon} />
                        Sent
                      </>
                    ) : completedLeads[lead._id] === 'loading' ? (
                      <>
                        <div className={styles.spinner}></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageCircle size={16} className={styles.buttonIcon} />
                        WhatsApp
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PopupComponent>
  );
};

export default BulkShareModal;